import { useMemo, useRef } from 'react';

import type { DecompositionNode } from 'api/data/projects/types';

import type { ChildrenPaginationInfo, ItemMeta, RichTreeItem } from '../types';
import { applyChildrenLimit } from '../utils/applyChildrenLimit';
import { toTreeItem } from '../utils/toTreeItem';
import { isErrorRow, isPlaceholder, isShowMore } from '../utils/treeId';

type Input = {
  roots: DecompositionNode[];
  loadedChildren: Record<string, RichTreeItem[]>;
  expandedParents: Set<string>;
  childrenPagination: Record<string, ChildrenPaginationInfo>;
  titlePlaceholder?: string;
};

type Output = {
  /** Tree after attach + Show-more/less synthesis. Used for rendering. */
  treeItems: RichTreeItem[];
  itemMetaMap: Record<string, ItemMeta>;
};

export function useTreeItems({
  roots,
  loadedChildren,
  expandedParents,
  childrenPagination,
  titlePlaceholder
}: Input): Output {
  const metaCache = useRef<Map<string, ItemMeta>>(new Map());

  const { treeItems, itemMetaMap } = useMemo(() => {
    // 1. Attach loaded children recursively.
    function attach(items: RichTreeItem[]): RichTreeItem[] {
      return items.map((item) => {
        const loaded = loadedChildren[item.id];
        if (loaded) return { ...item, children: attach(loaded) };
        return item;
      });
    }
    const attached = attach(roots.map((r) => toTreeItem(r, null, titlePlaceholder)));

    // 2. Apply Show-more/less synthesis.
    const tree = applyChildrenLimit(attached, expandedParents, childrenPagination);

    // 3. Collect ItemMeta from the final tree with stable-identity caching.
    const cache = metaCache.current;
    const seen = new Set<string>();
    const m: Record<string, ItemMeta> = {};

    function collectMeta(items: RichTreeItem[]) {
      for (const item of items) {
        if (!isPlaceholder(item.id) && !isShowMore(item.id) && !isErrorRow(item.id)) {
          seen.add(item.id);
          const cached = cache.get(item.id);
          if (
            cached &&
            cached.dataNodeId === item.dataNodeId &&
            cached.autoId === item.autoId &&
            cached.title === item.title &&
            cached.titlePlaceholder === item.titlePlaceholder
          ) {
            m[item.id] = cached;
          } else {
            const fresh: ItemMeta = {
              dataNodeId: item.dataNodeId,
              autoId: item.autoId,
              title: item.title,
              titlePlaceholder: item.titlePlaceholder
            };
            cache.set(item.id, fresh);
            m[item.id] = fresh;
          }
        }
        if (item.children) collectMeta(item.children);
      }
    }
    collectMeta(tree);

    if (cache.size !== seen.size) {
      for (const key of cache.keys()) {
        if (!seen.has(key)) cache.delete(key);
      }
    }

    return { treeItems: tree, itemMetaMap: m };
  }, [roots, loadedChildren, expandedParents, childrenPagination, titlePlaceholder]);

  return { treeItems, itemMetaMap };
}
